import { useState } from "react";
import { api, getErrorMessage } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Upload, FileSpreadsheet, Loader2, CheckCircle2, AlertTriangle, Undo2, Package, Building2 } from "lucide-react";
import { toast } from "sonner";

const fmtMoney = (v) =>
  v == null ? "—" : `${Number(v).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;

export const ImportSuppliersPage = () => {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);
  const [supplierMap, setSupplierMap] = useState({}); // normalized_name -> __create__ | existing_id

  const reset = () => {
    setFile(null);
    setPreview(null);
    setResult(null);
    setSupplierMap({});
  };

  const uploadPreview = async () => {
    if (!file) return;
    setUploading(true);
    setPreview(null);
    setResult(null);
    try {
      const r = await api.previewSuppliersImport(file);
      setPreview(r.data);
      const next = {};
      (r.data.suppliers || []).forEach((s) => {
        next[s.name.toLowerCase().trim()] = s.existing_id || "__create__";
      });
      setSupplierMap(next);
      toast.success(`${r.data.total_rows} ligne(s) analysée(s) — ${r.data.suppliers.length} fournisseur(s)`);
    } catch (e) {
      toast.error(getErrorMessage(e));
    } finally {
      setUploading(false);
    }
  };

  const doImport = async () => {
    if (!preview) return;
    setImporting(true);
    try {
      const r = await api.confirmSuppliersImport({
        rows: preview.rows,
        supplier_mapping: supplierMap,
      });
      setResult(r.data);
      toast.success(
        `${r.data.created_suppliers} nouveau(x) fournisseur(s), ` +
        `${r.data.created_products} produit(s), ${r.data.created_prices} prix importés`
      );
    } catch (e) {
      toast.error(getErrorMessage(e));
    } finally {
      setImporting(false);
    }
  };

  const rollback = async () => {
    if (!result?.import_batch_id) return;
    if (!window.confirm("Annuler l'import (supprime les fournisseurs/produits/prix créés) ?")) return;
    try {
      await api.rollbackSuppliersImport(result.import_batch_id);
      toast.success("Import annulé");
      reset();
    } catch (e) {
      toast.error(getErrorMessage(e));
    }
  };

  return (
    <div className="space-y-6 p-6">
      <div>
        <h1 className="text-3xl font-heading">Import fournisseurs & tarifs</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Importez un fichier Excel (.xlsx) ou CSV avec vos fournisseurs, produits et historique de prix.
        </p>
      </div>

      {/* Instructions */}
      <Alert className="border-blue-200 bg-blue-50/50">
        <FileSpreadsheet className="h-4 w-4 text-blue-600" />
        <AlertDescription className="text-sm">
          <strong>Colonnes attendues</strong> : <code>fournisseur</code>, <code>reference produit</code>, <code>type de produit</code>,{" "}
          <code>nom</code>, <code>unité</code> (m², kg, L...). Toute colonne dont l'en-tête est une année
          (<code>PRIX 2023</code>, <code>prix 2024</code>) ou une date (<code>2024-06-01</code>) sera traitée comme un prix historique.
        </AlertDescription>
      </Alert>

      {/* Upload */}
      {!preview && (
        <Card>
          <CardHeader><CardTitle>1. Téléverser le fichier</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <input
              type="file"
              accept=".xlsx,.xls,.csv"
              onChange={(e) => { setFile(e.target.files?.[0] || null); setResult(null); }}
              className="block w-full text-sm text-slate-600 file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-medium file:bg-primary file:text-primary-foreground hover:file:bg-primary/90 cursor-pointer"
              data-testid="import-suppliers-file-input"
            />
            <Button onClick={uploadPreview} disabled={!file || uploading} data-testid="import-suppliers-preview-btn">
              {uploading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />}
              Prévisualiser
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Preview */}
      {preview && !result && (
        <>
          <Card>
            <CardHeader className="flex-row flex items-center justify-between">
              <CardTitle>2. Vérification</CardTitle>
              <Button variant="ghost" size="sm" onClick={reset} data-testid="import-suppliers-reset">
                Réinitialiser
              </Button>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="p-3 bg-slate-50 rounded border">
                  <div className="text-xs text-muted-foreground">Lignes exploitables</div>
                  <div className="text-2xl font-heading" data-testid="stat-rows">{preview.total_rows}</div>
                </div>
                <div className="p-3 bg-slate-50 rounded border">
                  <div className="text-xs text-muted-foreground">Fournisseurs</div>
                  <div className="text-2xl font-heading">{preview.suppliers.length}</div>
                </div>
                <div className="p-3 bg-slate-50 rounded border">
                  <div className="text-xs text-muted-foreground">Colonnes de prix</div>
                  <div className="text-2xl font-heading">{preview.price_columns.length}</div>
                </div>
                <div className="p-3 bg-slate-50 rounded border">
                  <div className="text-xs text-muted-foreground">Points de prix détectés</div>
                  <div className="text-2xl font-heading">
                    {preview.suppliers.reduce((acc, s) => acc + (s.price_points || 0), 0)}
                  </div>
                  <div className="text-[10px] text-muted-foreground mt-0.5">
                    (les doublons seront fusionnés à l'import)
                  </div>
                </div>
              </div>

              {preview.warnings?.length > 0 && (
                <Alert className="border-amber-200 bg-amber-50/50">
                  <AlertTriangle className="h-4 w-4 text-amber-600" />
                  <AlertDescription>
                    <ul className="list-disc ml-5 text-sm space-y-0.5">
                      {preview.warnings.map((w, i) => <li key={i}>{w}</li>)}
                    </ul>
                  </AlertDescription>
                </Alert>
              )}

              {/* Price columns detected */}
              <div>
                <div className="text-sm font-medium mb-2 flex items-center gap-2">
                  <Package className="h-4 w-4" /> Colonnes de prix détectées
                </div>
                <div className="flex flex-wrap gap-1">
                  {preview.price_columns.map((p, i) => (
                    <Badge key={i} variant="outline" className="text-[11px]" data-testid={`price-col-${i}`}>
                      {p.label} → {p.date}
                    </Badge>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Suppliers */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building2 className="h-5 w-5" /> Fournisseurs ({preview.suppliers.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="max-h-80 overflow-y-auto border rounded">
                <table className="w-full text-sm">
                  <thead className="sticky top-0 bg-slate-50 border-b">
                    <tr>
                      <th className="text-left p-2">Nom</th>
                      <th className="text-right p-2">Produits</th>
                      <th className="text-right p-2">Points prix</th>
                      <th className="text-left p-2">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.suppliers.map((s, i) => (
                      <tr key={i} className="border-b hover:bg-slate-50" data-testid={`supplier-row-${i}`}>
                        <td className="p-2 font-medium">{s.name}</td>
                        <td className="p-2 text-right">{s.product_count}</td>
                        <td className="p-2 text-right">{s.price_points}</td>
                        <td className="p-2">
                          {s.existing_id ? (
                            <Badge className="bg-emerald-100 text-emerald-800 border-emerald-300">
                              Fusion (existant)
                            </Badge>
                          ) : (
                            <Badge variant="outline">Nouveau</Badge>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>

          {/* Sample products */}
          <Card>
            <CardHeader>
              <CardTitle>Échantillon (10 premières lignes)</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="bg-slate-50 border-b">
                    <tr>
                      <th className="text-left p-2">Ligne</th>
                      <th className="text-left p-2">Fournisseur</th>
                      <th className="text-left p-2">Référence</th>
                      <th className="text-left p-2">Nom</th>
                      <th className="text-left p-2">Type</th>
                      <th className="text-left p-2">Unité</th>
                      <th className="text-right p-2">Dernier prix</th>
                      <th className="text-right p-2">Nb prix</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.products_sample.map((p, i) => {
                      const lastPrice = p.prices?.[p.prices.length - 1];
                      return (
                        <tr key={i} className="border-b">
                          <td className="p-2 text-muted-foreground">{p._excel_row}</td>
                          <td className="p-2">{p.supplier}</td>
                          <td className="p-2 font-mono">{p.reference || "—"}</td>
                          <td className="p-2">{p.name}</td>
                          <td className="p-2">{p.product_type || "—"}</td>
                          <td className="p-2">{p.unit || "—"}</td>
                          <td className="p-2 text-right font-mono">
                            {lastPrice ? `${fmtMoney(lastPrice.amount)} (${lastPrice.date})` : "—"}
                          </td>
                          <td className="p-2 text-right">{p.prices?.length || 0}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>

          <div className="flex gap-3 justify-end">
            <Button variant="outline" onClick={reset}>Annuler</Button>
            <Button onClick={doImport} disabled={importing} data-testid="import-suppliers-confirm-btn">
              {importing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CheckCircle2 className="h-4 w-4 mr-2" />}
              Lancer l'import
            </Button>
          </div>
        </>
      )}

      {/* Result */}
      {result && (
        <Card className="border-emerald-200 bg-emerald-50/30">
          <CardHeader>
            <CardTitle className="text-emerald-800 flex items-center gap-2">
              <CheckCircle2 className="h-5 w-5" /> Import terminé
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              <Stat label="Fournisseurs créés" value={result.created_suppliers} testid="stat-created-suppliers" />
              <Stat label="Fournisseurs existants" value={result.reused_suppliers} testid="stat-reused-suppliers" />
              <Stat label="Produits créés" value={result.created_products} testid="stat-created-products" />
              <Stat label="Produits mis à jour" value={result.updated_products} />
              <Stat label="Prix ajoutés" value={result.created_prices} testid="stat-created-prices" />
            </div>
            <div className="flex gap-3 pt-2">
              <Button variant="outline" onClick={reset} data-testid="import-suppliers-new">
                Nouvel import
              </Button>
              <Button
                variant="outline"
                className="text-red-600 border-red-200 hover:bg-red-50"
                onClick={rollback}
                data-testid="import-suppliers-rollback"
              >
                <Undo2 className="h-4 w-4 mr-2" /> Annuler cet import
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

const Stat = ({ label, value, testid }) => (
  <div className="p-3 bg-white rounded border">
    <div className="text-xs text-muted-foreground">{label}</div>
    <div className="text-2xl font-heading" data-testid={testid}>{value}</div>
  </div>
);

export default ImportSuppliersPage;
